import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { has, isEqual } from 'lodash';

import { useParams } from 'react-router-dom';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import Dialog from 'components/ui/Dialog';
import Form from 'components/ui/Form';
import { InputAdornment, TextField } from '@material-ui/core';
import { FormSectionTitle, GridContainer } from 'styles/form';
import {
  getService,
  updateService,
  createService,
  deleteService,
} from 'api/service';
import { useSnackBar } from 'context/SnackBarContext';
import { initialValues, validationSchema } from '../service.config';

const Service = ({
  modalOpen,
  handleModalClose,
  blockRouteChange,
  setBlockRouteChange,
}) => {
  const { id } = useParams();

  const [service, setService] = useState();
  const { setMessage, setOpenSnackBar, setSeverity } = useSnackBar();

  useEffect(async () => {
    if (id) {
      const { data } = await getService(id);
      setService(data);
    }
  }, []);

  const handleSave = async (values) => {
    if (service) {
      try {
        await updateService(id, values);
        setMessage('Saved changes');
        setSeverity('success');
      } catch (error) {
        console.error(error);
        setMessage('Something went wrong. Failed to save changes.');
        setSeverity('error');
      }
    } else {
      try {
        await createService(values);
        setMessage('Service created');
        setSeverity('success');
      } catch (error) {
        console.error(error);
        setMessage('Something went wrong. Failed to create service.');
        setSeverity('error');
      }
    }

    setOpenSnackBar(true);
    handleModalClose(true);
  };

  const handleDelete = async () => {
    try {
      await deleteService(id);
      setMessage('Service deleted');
      setSeverity('success');
    } catch (error) {
      console.error(error);
      setMessage('Something went wrong. Failed to delete service.');
      setSeverity('error');
    }

    handleModalClose(true);
    setOpenSnackBar(true);
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Formik
        initialValues={initialValues(service)}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSave}
      >
        {({ values, errors, handleChange, submitForm, touched }) => {
          const { name, duration, equipmentType } = values;

          const initValues = initialValues(service);

          const unsavedChange = !isEqual(
            {
              ...values,
              duration: Number(duration),
            },
            { ...initValues, duration: Number(initValues.duration) },
          );

          if (unsavedChange) {
            setBlockRouteChange(true);
          } else {
            setBlockRouteChange(false);
          }

          return (
            <Dialog
              open={modalOpen}
              handleClose={handleModalClose}
              submitForm={submitForm}
              typeText="Service"
              handleDelete={handleDelete}
              blockRouteChange={blockRouteChange}
            >
              <FormSectionTitle>Service</FormSectionTitle>
              <Form fullWidth>
                <GridContainer>
                  <TextField
                    color="primary"
                    error={touched.name && has(errors, 'name')}
                    helperText={touched.name && errors.name}
                    label="Service Name"
                    name="name"
                    onChange={handleChange}
                    value={name}
                    variant="outlined"
                    required
                  />

                  <TextField
                    label="Default Duration"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">Mins</InputAdornment>
                      ),
                    }}
                    name="duration"
                    error={touched.duration && has(errors, 'duration')}
                    helperText={touched.duration && errors.duration}
                    inputProps={{ step: 5 }}
                    onChange={handleChange}
                    value={duration}
                    variant="outlined"
                    type="number"
                    required
                  />

                  <TextField
                    color="primary"
                    error={
                      touched.equipmentType && has(errors, 'equipmentType')
                    }
                    fullWidth
                    helperText={touched.equipmentType && errors.equipmentType}
                    label="Equipment Type"
                    name="equipmentType"
                    onChange={handleChange}
                    value={equipmentType}
                    variant="outlined"
                  />
                </GridContainer>
              </Form>
            </Dialog>
          );
        }}
      </Formik>
    </MuiPickersUtilsProvider>
  );
};

export default Service;
